import { useAppStore } from '@/store/useAppStore.ts'
import { Badge } from '@/components/shared/Badge.tsx'
import { CollapsibleSection } from '@/components/shared/CollapsibleSection.tsx'
import { FormulaDisplay } from '@/components/shared/FormulaDisplay.tsx'
import { NumericDisplay } from 'calc-controls'
import React from 'react'
import type { RuntimeResult } from '@brt/shared-types'
import { getChemistryPreset } from '@brt/shared-logic'

function formatCRate(cRate: number): string {
  return `${cRate.toFixed(2)}C`
}

function CRateBadge({ cRate }: { cRate: number }) {
  const chemistry = useAppStore((s) => s.battery.chemistry)
  const preset = getChemistryPreset(chemistry)

  let status: 'safe' | 'caution' | 'danger' = 'safe'
  if (cRate > preset.peakCRate) status = 'danger'
  else if (cRate > preset.maxSafeCRate) status = 'caution'

  const variant = status === 'safe' ? 'success' : status === 'caution' ? 'warning' : 'danger'

  return <Badge variant={variant}>{formatCRate(cRate)} — {status}</Badge>
}

function ConfidenceLabel({ level }: { level: string }) {
  const variant = level === 'ideal' ? 'info' : level === 'adjusted' ? 'warning' : 'danger'
  return <Badge variant={variant}>{level}</Badge>
}

function CycleBalanceIndicator(): React.ReactNode {
  const runtimeResult = useAppStore((s) => s.runtimeResult)
  const chargeTimeResult = useAppStore((s) => s.chargeTimeResult)

  if (!runtimeResult || !chargeTimeResult) return null
  const runtimeHours = runtimeResult.runtimeHours
  const chargeHours = chargeTimeResult.chargeTimeHours
  if (runtimeHours == null || runtimeHours <= 0 || chargeHours == null || chargeHours <= 0) {
    return null
  }

  const ratio = runtimeHours / chargeHours
  let verdict: string
  let variant: 'success' | 'warning' | 'danger'
  if (ratio >= 1) {
    verdict = 'sustains back-to-back cycles'
    variant = 'success'
  } else if (ratio >= 0.5) {
    verdict = 'recharge slower than discharge'
    variant = 'warning'
  } else {
    verdict = 'cannot sustain cyclic use'
    variant = 'danger'
  }

  return (
    <div className="runtime-card__cycle-balance" data-testid="cycle-balance-indicator">
      <span className="runtime-card__cycle-balance-ratio">
        Cycle balance: {runtimeHours.toFixed(1)}h run / {chargeHours.toFixed(1)}h charge
      </span>
      <Badge variant={variant}>{verdict}</Badge>
    </div>
  )
}

function DeratingBreakdown({ result }: { result: RuntimeResult }) {
  const steps = result.deratingSteps
  const factors = result.deratingFactors

  const entries = [
    { label: 'Raw Capacity', value: result.rawCapacityAh, factor: null },
    { label: 'After DoD', value: steps.afterDoD, factor: factors.dodFactor },
    { label: 'After SoC', value: steps.afterSoC, factor: factors.socFactor },
    { label: 'After SoH', value: steps.afterSoH, factor: factors.sohFactor },
    { label: 'After Temperature', value: steps.afterTemperature, factor: factors.temperatureFactor },
    { label: 'After Self-discharge', value: steps.afterSelfDischarge, factor: factors.selfDischargeFactor },
    { label: 'After Peukert', value: steps.afterPeukert, factor: factors.peukertFactor },
    { label: 'After Efficiency (load)', value: steps.afterEfficiency, factor: factors.efficiencyFactor },
    { label: 'After Reserve', value: steps.afterReserve, factor: factors.reserveFactor },
  ]

  return (
    <div className="derating-breakdown">
      {entries.map((entry) => (
        entry.factor !== null && entry.factor !== 1 ? (
          <div key={entry.label} className="derating-breakdown__row">
            <span className="derating-breakdown__label">{entry.label}</span>
            <span className="derating-breakdown__factor">×{entry.factor.toFixed(4)}</span>
            <span className="derating-breakdown__value">{entry.value.toFixed(2)} Ah</span>
          </div>
        ) : null
      ))}
    </div>
  )
}

export function RuntimeCard() {
  const result = useAppStore((s) => s.runtimeResult)
  const solveMode = useAppStore((s) => s.solveMode)
  const batterySizeResult = useAppStore((s) => s.batterySizeResult)
  const maxLoadResult = useAppStore((s) => s.maxLoadResult)
  const showFormula = useAppStore((s) => s.showFormulaExplanation)

  if (!result) {
    return (
      <div className="runtime-card runtime-card--empty">
        <div className="runtime-card__empty-content">
          <p className="runtime-card__placeholder">Enter battery and load values to calculate runtime.</p>
          <p className="runtime-card__guidance">Runtime is calculated automatically when you enter values in the input panel on the left.</p>
          <button
            type="button"
            className="runtime-card__cta"
            onClick={() => window.dispatchEvent(new CustomEvent('brt:focus-input'))}
          >
            Start entering values →
          </button>
        </div>
      </div>
    )
  }

  if (solveMode === 'BATTERY_SIZE' && batterySizeResult) {
    return (
      <div className="runtime-card">
        <h2 className="runtime-card__title">Battery Size</h2>
        <div className="runtime-card__hero">
          <span className="runtime-card__value">{batterySizeResult.requiredCapacityAh?.toFixed(1) ?? '—'}</span>
          <span className="runtime-card__unit">Ah</span>
        </div>
        {batterySizeResult.nearestSizes.length > 0 && (
          <>
            <p className="result-caption">Minimum rated capacity needed to meet the target runtime after all derating factors.</p>
            <div className="runtime-card__sizes">
            <h3 className="runtime-card__subtitle">Nearest Standard Sizes</h3>
            {batterySizeResult.nearestSizes.map((size) => (
              <div key={size.capacityAh} className="runtime-card__size-row">
                <span>{size.capacityAh} Ah ({size.label})</span>
                <span>{size.estimatedRuntimeHours.toFixed(1)} h</span>
                <Badge variant={size.passFail === 'pass' ? 'success' : 'warning'}>{size.passFail}</Badge>
              </div>
            ))}
            </div>
          </>
        )}
      </div>
    )
  }

  if (solveMode === 'MAX_LOAD' && maxLoadResult) {
    return (
      <div className="runtime-card">
        <h2 className="runtime-card__title">Maximum Load</h2>
        <div className="runtime-card__hero">
          <span className="runtime-card__value">{maxLoadResult.maxLoadA?.toFixed(2) ?? '—'}</span>
          <span className="runtime-card__unit">A</span>
        </div>
        {maxLoadResult.maxLoadW != null && (
          <p className="runtime-card__secondary">{maxLoadResult.maxLoadW.toFixed(1)} W</p>
        )}
        <p className="result-caption">Maximum continuous current (A) and power (W) the battery can sustain for the target runtime.</p>
      </div>
    )
  }

  const runtimeColor = result.runtimeHours != null
    ? result.runtimeHours < 1 ? 'amber' : 'green'
    : 'default'

  return (
    <div className="runtime-card">
      <div className="runtime-card__header">
        <h2 className="runtime-card__title">Estimated Runtime</h2>
        <ConfidenceLabel level={result.confidence} />
      </div>

      <div className={`runtime-card__hero runtime-card__hero--${runtimeColor}`}>
        <span className="runtime-card__value">{result.runtimeFormatted}</span>
      </div>

      <CycleBalanceIndicator />

      <div className="runtime-card__meta">
        <NumericDisplay label="Effective Capacity" value={result.effectiveCapacityAh} unit="Ah" decimals={2} />
        <NumericDisplay label="Effective Load" value={result.effectiveLoadA} unit="A" decimals={2} />
        <div className="runtime-card__stat">
          <span className="runtime-card__stat-label">C-Rate</span>
          <CRateBadge cRate={result.cRate} />
        </div>
      </div>

      <p className="result-caption">
        Effective capacity (Ah) is the rated capacity after applying all derating factors. 
        C-rate is the discharge rate relative to rated capacity in Ah.
      </p>

      {result.formulaExplanation && (
        <CollapsibleSection title="Formula Explanation" defaultOpen={showFormula}>
          <FormulaDisplay formula={result.formulaExplanation} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Derating Breakdown">
        <p className="result-caption">Each factor reduces effective capacity step-wise from the rated Ah value.</p>
        <DeratingBreakdown result={result} />
      </CollapsibleSection>

      {result.assumptionsList.length > 0 && (
        <CollapsibleSection title="Assumptions">
          <ul className="runtime-card__assumptions">
            {result.assumptionsList.map((assumption, i) => (
              <li key={i}>{assumption}</li>
            ))}
          </ul>
        </CollapsibleSection>
      )}
    </div>
  )
}
